import React, { useEffect, useState, useCallback, useMemo } from "react";
import { RefreshCw, Trash2, ChevronRight, ChevronDown, Filter, Terminal, CheckCircle2, XCircle, Clock } from "lucide-react";
import { dataApi, formatApiError } from "../api/client";

const ENDPOINT_LABELS = {
  "/api/bot/create-ride": "Crear carrera",
  "/api/whatsapp/2chat/webhook": "Webhook 2chat",
  "/api/bot/cancel-ride": "Cancelar carrera",
  "/api/bot/ride-status": "Estado carrera",
  "/api/bot/zones": "Listar zonas",
};

function StatusPill({ code }) {
  const ok = code >= 200 && code < 300;
  const warn = code >= 400 && code < 500;
  const cls = ok
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : warn
      ? "bg-amber-100 text-amber-700 border-amber-200"
      : "bg-rose-100 text-rose-700 border-rose-200";
  return (
    <span data-testid={`log-status-${code}`} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-semibold border ${cls}`}>
      {ok ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
      {code}
    </span>
  );
}

function JsonBlock({ data, testid }) {
  const text = useMemo(() => {
    try {
      return typeof data === "string" ? data : JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  }, [data]);
  return (
    <pre
      data-testid={testid}
      className="bg-neutral-950 text-emerald-300 text-xs p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words border border-neutral-800 font-mono"
      style={{ maxHeight: 320 }}
    >
      {text || "(vacío)"}
    </pre>
  );
}

function LogRow({ log, expanded, onToggle }) {
  const shortEndpoint = log.endpoint?.replace("/api", "") || "-";
  const ts = log.timestamp?.replace("T", " ").slice(0, 19);
  return (
    <>
      <tr
        data-testid={`botlog-row-${log.id}`}
        onClick={() => onToggle(log.id)}
        className="cursor-pointer hover:bg-neutral-50 border-b border-neutral-100"
      >
        <td className="px-3 py-2 w-6">
          {expanded ? <ChevronDown className="w-4 h-4 text-neutral-400" /> : <ChevronRight className="w-4 h-4 text-neutral-400" />}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-neutral-600 whitespace-nowrap">{ts}</td>
        <td className="px-3 py-2"><StatusPill code={log.response_status} /></td>
        <td className="px-3 py-2 text-xs font-mono text-neutral-700">{log.method}</td>
        <td className="px-3 py-2 text-xs">
          <span className="font-semibold text-[#1A237E]">{ENDPOINT_LABELS[log.endpoint] || shortEndpoint}</span>
          <div className="text-[10px] text-neutral-400 font-mono">{shortEndpoint}</div>
        </td>
        <td className="px-3 py-2 text-xs font-mono text-neutral-700">{log.phone || "—"}</td>
        <td className="px-3 py-2 text-xs text-neutral-500 whitespace-nowrap">
          <Clock className="inline w-3 h-3 mr-1" />{log.duration_ms}ms
        </td>
      </tr>
      {expanded && (
        <tr data-testid={`botlog-expanded-${log.id}`} className="bg-neutral-50">
          <td colSpan={7} className="px-4 py-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">Body recibido (raw)</div>
                <JsonBlock data={log.raw_body || "(sin body)"} testid={`log-raw-${log.id}`} />
              </div>
              <div>
                <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">Datos parseados</div>
                <JsonBlock data={log.parsed_data} testid={`log-parsed-${log.id}`} />
              </div>
              {log.query_params && Object.keys(log.query_params).length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">Query params</div>
                  <JsonBlock data={log.query_params} testid={`log-query-${log.id}`} />
                </div>
              )}
              {log.headers && (
                <div>
                  <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">Headers</div>
                  <JsonBlock data={log.headers} testid={`log-headers-${log.id}`} />
                </div>
              )}
              <div className="lg:col-span-2">
                <div className="text-[11px] font-semibold text-neutral-500 uppercase tracking-wider mb-1">Respuesta del backend</div>
                <JsonBlock data={log.response_body} testid={`log-response-${log.id}`} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function BotLogsView() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [filterEndpoint, setFilterEndpoint] = useState("");
  const [filterPhone, setFilterPhone] = useState("");
  const [filterOnlyErrors, setFilterOnlyErrors] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 100 };
      if (filterEndpoint) params.endpoint = filterEndpoint;
      if (filterPhone) params.phone = filterPhone;
      if (filterOnlyErrors) params.status_min = 400;
      const [r, s] = await Promise.all([dataApi.botLogs(params), dataApi.botLogsStats()]);
      setLogs(r.logs || []);
      setStats(s);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filterEndpoint, filterPhone, filterOnlyErrors]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const handleClear = async () => {
    if (!window.confirm("¿Borrar TODOS los logs del bot? Esta acción no se puede deshacer.")) return;
    try {
      await dataApi.botLogsClear();
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div data-testid="bot-logs-view" className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-[#1A237E] flex items-center gap-2">
            <Terminal className="w-6 h-6" />
            Bot Logs · Diagnóstico 2chat
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Cada request que llega desde 2chat.io al backend queda registrado acá. Últimos 100 eventos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-neutral-600 select-none">
            <input
              type="checkbox"
              data-testid="botlogs-autorefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh 5s
          </label>
          <button
            data-testid="botlogs-refresh-btn"
            onClick={load}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-white border border-neutral-200 rounded-lg hover:bg-neutral-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </button>
          <button
            data-testid="botlogs-clear-btn"
            onClick={handleClear}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200 rounded-lg hover:bg-rose-100"
          >
            <Trash2 className="w-3.5 h-3.5" /> Limpiar
          </button>
        </div>
      </div>

      {/* KPIs 24h */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div data-testid="botlogs-kpi-total" className="bg-white border border-neutral-200 rounded-lg p-3">
            <div className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Total 24h</div>
            <div className="text-2xl font-bold text-[#1A237E]">{stats.total || 0}</div>
          </div>
          <div data-testid="botlogs-kpi-errors" className="bg-white border border-neutral-200 rounded-lg p-3">
            <div className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Errores 24h</div>
            <div className="text-2xl font-bold text-rose-600">{stats.errors || 0}</div>
          </div>
          {stats.by_endpoint?.["/api/bot/create-ride"] && (
            <div className="bg-white border border-neutral-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Crear carrera</div>
              <div className="text-sm">
                <span className="font-bold text-emerald-600">{stats.by_endpoint["/api/bot/create-ride"].ok || 0}</span>
                <span className="text-neutral-400 mx-1">·</span>
                <span className="font-bold text-rose-600">{stats.by_endpoint["/api/bot/create-ride"].error || 0}</span>
                <span className="text-[10px] text-neutral-500 ml-2">ok · err</span>
              </div>
            </div>
          )}
          {stats.by_endpoint?.["/api/whatsapp/2chat/webhook"] && (
            <div className="bg-white border border-neutral-200 rounded-lg p-3">
              <div className="text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Webhook</div>
              <div className="text-sm">
                <span className="font-bold text-emerald-600">{stats.by_endpoint["/api/whatsapp/2chat/webhook"].ok || 0}</span>
                <span className="text-neutral-400 mx-1">·</span>
                <span className="font-bold text-rose-600">{stats.by_endpoint["/api/whatsapp/2chat/webhook"].error || 0}</span>
                <span className="text-[10px] text-neutral-500 ml-2">ok · err</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-neutral-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <Filter className="w-4 h-4 text-neutral-400" />
        <input
          data-testid="botlogs-filter-endpoint"
          type="text"
          placeholder="Filtrar por endpoint (ej: create-ride)"
          value={filterEndpoint}
          onChange={(e) => setFilterEndpoint(e.target.value)}
          className="px-2 py-1 text-xs border border-neutral-200 rounded flex-1 min-w-[180px]"
        />
        <input
          data-testid="botlogs-filter-phone"
          type="text"
          placeholder="Teléfono (+51...)"
          value={filterPhone}
          onChange={(e) => setFilterPhone(e.target.value)}
          className="px-2 py-1 text-xs border border-neutral-200 rounded w-[180px]"
        />
        <label className="inline-flex items-center gap-1 text-xs text-neutral-600 select-none">
          <input
            data-testid="botlogs-filter-errors"
            type="checkbox"
            checked={filterOnlyErrors}
            onChange={(e) => setFilterOnlyErrors(e.target.checked)}
            className="rounded"
          />
          Solo errores (4xx/5xx)
        </label>
      </div>

      {error && (
        <div data-testid="botlogs-error" className="p-3 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          {error}
        </div>
      )}

      {/* Tabla */}
      <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="px-3 py-2 w-6"></th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Timestamp</th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Status</th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Método</th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Endpoint</th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Teléfono</th>
              <th className="px-3 py-2 text-[10px] uppercase font-semibold text-neutral-500 tracking-wider">Duración</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-sm text-neutral-400">
                  {loading ? "Cargando..." : "Sin registros. Cuando 2chat envíe una petición aparecerá acá."}
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <LogRow
                  key={log.id}
                  log={log}
                  expanded={expandedId === log.id}
                  onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
